import { useSideEffectOnMount } from "@web-monorepo/hooks";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useContext,
  type ReactNode,
  type ReactElement,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { autoTranslate } from "@web-monorepo/translation-runtime";
import { DDSButton, Sizes, Variants } from "../../Buttons/DDSButton/DDSButton";
import classes from "./DDSDialog.module.css";
import { DDSIconButton } from "../../Buttons/DDSIconButton/DDSIconButton";
import { IconArrowLeft, IconX } from "@web-monorepo/dds-icons";
import { getSite, logEvent, logMessage } from "@classdojo/log-client";
import { useEventListener, useResizeObserver, useWatch } from "@web-monorepo/hooks";

import { TranslatedString } from "@web-monorepo/i18next";
import { clsx } from "clsx";
import { startManaging, stopManaging } from "./overflowManager";
import { ModalContextProvider } from "../ModalContext";
import { DDSScrollLayout } from "../../Layout/DDSScrollLayout/DDSScrollLayout";

// Create a context for managing dialog state
interface DDSDialogContextType {
  open: boolean;
  onOpenChange: (flag: boolean) => void;
  contentRef: React.RefObject<HTMLDivElement>;
}

const DDSDialogContext = React.createContext<DDSDialogContextType>({
  open: false,
  onOpenChange: () => {},
  contentRef: { current: null },
});

export interface DDSDialogProps {
  /** Typically DDSDialogTrigger (optional) + DDSDialogContent, but accepts ReactNode to support conditional rendering patterns. */
  children: React.ReactNode;
  "data-name": string;
  "event-metadata"?: object;
  experiments?: string[];
  /**
   * Whether the dialog is open.
   */
  open?: boolean;
  /**
   * Whether the dialog is open by default.
   */
  defaultOpen?: boolean;
  /**
   * Callback function to handle the dialog open state change.
   */
  onOpenChange?(open: boolean): void;
  /**
   * Callback function to run when the dialog opens.
   */
  onOpen?: () => void;
  /**
   * Callback function to run when the dialog closes.
   */
  onClose?: () => void;
  /**
   * The label for the dialog.
   */
  label?: string;
  /**
   * If true, this dialog will manage hiding overflow on the #root element
   * when it is open.
   */
  manageRootOverflowForMobile?: boolean;
}

const DDSDialogRoot = Dialog.Root;
const DDSDialogTrigger = Dialog.Trigger;

function DDSDialog({ children, ...props }: DDSDialogProps) {
  const {
    "data-name": dataName,
    "event-metadata": metadata,
    experiments,
    defaultOpen,
    onOpenChange,
    onOpen,
    onClose,
    manageRootOverflowForMobile,
  } = props;

  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);

  const isControlled = typeof props.open !== "undefined";
  const isOpen = isControlled ? (props.open ?? false) : internalOpen;

  const savedElement = useRef<(typeof document)["activeElement"]>();
  const contentRef = useRef<HTMLDivElement>(null);

  const rootElementRef = useRef<HTMLElement | null>(null);

  useSideEffectOnMount(() => {
    // Store the root element in a ref once when any dialog mounts
    // This assumes #root exists. Consider error handling if it might not.
    if (!rootElementRef.current) {
      rootElementRef.current = document.getElementById("root");
    }
  });

  // This is required for refocusing on triggers if they are not
  // specified with explicit DDSDialogTriggers (i.e. bare buttons setting state
  // that triggers the modal)
  useEffect(() => {
    if (typeof isOpen !== "boolean") return;
    if (isOpen) {
      savedElement.current = document.activeElement;
    } else {
      if (savedElement.current && "focus" in savedElement.current && typeof savedElement.current.focus === "function") {
        savedElement.current.focus();
      }
    }
  }, [isOpen]);

  // Handle open state changes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      onOpenChange?.(newOpen);
      if (newOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [setInternalOpen, onOpenChange, onOpen, onClose],
  );

  const dialogContext = useMemo(
    () => ({
      open: isOpen,
      onOpenChange: handleOpenChange,
      contentRef,
    }),
    [isOpen, handleOpenChange],
  );

  useWatch(
    isOpen,
    (newOpen, oldOpen) => {
      if (!dataName) {
        logMessage("WARN", `${getSite()}.dialog.missingDataName`);
        return;
      }
      if (!newOpen && !oldOpen) return;
      logEvent({
        eventName: `${getSite()}.dialog.${dataName}.${newOpen ? "open" : "close"}`,
        automatedEvent: true,
        metadata,
        experiments,
      });
    },
    { immediate: true },
  );

  useEffect(() => {
    if (manageRootOverflowForMobile) {
      startManaging();
      return () => {
        stopManaging();
      };
    }
  }, [manageRootOverflowForMobile]);

  return (
    <DDSDialogContext.Provider value={dialogContext}>
      <DDSDialogRoot open={isOpen} onOpenChange={handleOpenChange}>
        {children}
      </DDSDialogRoot>
    </DDSDialogContext.Provider>
  );
}

function useDialogContext() {
  const context = useContext(DDSDialogContext);
  if (context === undefined) {
    throw new Error("useDialogContext must be used within a DDSDialog");
  }
  return context;
}

type BaseDialogContentProps = Omit<Dialog.DialogContentProps, "onClose"> & {
  /** Typically DDSDialogHeader (+ other content) or DDSTabs, but accepts ReactNode to support conditional rendering patterns. */
  children: React.ReactNode;
  /**
   * When true, the dialog will maintain its initial calculated height
   * even when content size changes, preventing layout shifts
   */
  maintainInitialHeight?: boolean;
  contentClassName?: string;
  fullscreen?: boolean;
  /**
   * Visual variant for the dialog content layout.
   * - `"promotional"`: centered text, centered full-width CTA footer, full-width header images.
   *   Use for marketing, announcement, and upsell dialogs.
   */
  variant?: "promotional";
  ariaLabel?: "Do not use this, we enforce an aria-labelledby and the aria-label will be overridden";
  "aria-label"?: "Do not use this, we enforce an aria-labelledby and the aria-label will be overridden";
};

type RequireExplicitCloseProps = BaseDialogContentProps & {
  /**
   * Disables closing the dialog on background click, using the escape key, or clicking outside the dialog.
   * Best for modals where forms are being filled out and you don't want to lose the data on an erroneous outside click.
   */
  requireExplicitClose: boolean;
  onClose?: never;
};

type OnCloseProps = BaseDialogContentProps & {
  requireExplicitClose?: never;
  /**
   * If you're managing your modal state yourself, you can pass in an onClose function to handle the close event for clicking outside the dialog
   */
  onClose: () => void;
};

type DefaultProps = BaseDialogContentProps & {
  requireExplicitClose?: never;
  onClose?: never;
};

type DDSDialogContentProps = RequireExplicitCloseProps | OnCloseProps | DefaultProps;

function isDialogHeader(node: unknown): boolean {
  if (!React.isValidElement(node)) {
    return false;
  }
  const type = node.type as React.ComponentType & { displayName?: string };
  if (type === DDSDialogHeader || type === DDSDialogImageHeader) {
    return true;
  }
  // During HMR/Storybook, component reference can differ from module reference; displayName remains stable.
  return type?.displayName === "DDSDialogHeader" || type?.displayName === "DDSDialogImageHeader";
}

function DDSDialogContent({
  children,
  requireExplicitClose,
  maintainInitialHeight,
  onClose,
  className,
  contentClassName,
  fullscreen,
  variant,
  ...rest
}: DDSDialogContentProps) {
  // Runtime check for header
  const hasValidHeader = Array.isArray(children)
    ? isDialogHeader(children[0])
    : React.isValidElement(children) &&
      Array.isArray(children.props.children) &&
      isDialogHeader(children.props.children[0]);

  if (!hasValidHeader) {
    throw new TypeError(
      "DDSDialogContent requires DDSDialogHeader as its first child (or wrapped with <DDSTabs>). If you don't want to show the header title, you may pass `visuallyHideTitle` to the header.",
    );
  }

  const [initialHeight, setInitialHeight] = useState<number | null>(null);
  const { onOpenChange, contentRef } = useDialogContext();

  useLayoutEffect(() => {
    if (maintainInitialHeight && contentRef.current && !initialHeight) {
      const height = contentRef.current.offsetHeight;
      if (height > 0) {
        setInitialHeight(height);
      }
    }
  }, [maintainInitialHeight, initialHeight, children, contentRef]);

  const onInteractOutside: DDSDialogContentProps["onInteractOutside"] = (e) => {
    // Focusing inside a modal that is presented by this modal shouldn't close
    // this modal:
    if (e.type !== "dismissableLayer.focusOutside") {
      handleClose(e);
    }
  };

  const handleClose = (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    if (onClose) {
      onClose();
    }

    if (requireExplicitClose) {
      e.preventDefault();
    }
    e.stopPropagation();
  };

  useEventListener(
    "keydown",
    (keyEvent) => {
      if (keyEvent.key !== "Escape") return;
      if (!document.activeElement) return;

      if (contentRef.current?.contains(document.activeElement)) {
        keyEvent.stopPropagation();
        keyEvent.preventDefault();

        if (requireExplicitClose) {
          return;
        }

        // Use the context to close the dialog
        if (onClose) {
          onClose();
        } else {
          onOpenChange(false);
        }
      }
    },
    globalThis.window,
    true,
  );

  return (
    <Dialog.Portal>
      <Dialog.Overlay className={classes.overlay}>
        <Dialog.Content
          ref={contentRef}
          className={clsx(classes.container, fullscreen && classes.fullscreenDialog, className)}
          aria-describedby={undefined}
          onPointerDownOutside={handleClose}
          onInteractOutside={onInteractOutside}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const titleElement = contentRef.current?.querySelector('[role="dialog"] h2');
            if (titleElement instanceof HTMLElement) {
              titleElement.focus();
            }
          }}
          {...rest}
        >
          <DDSScrollLayout
            className={clsx(classes.content, variant === "promotional" && "ddsDialogPromotional", contentClassName)}
            style={maintainInitialHeight && initialHeight ? { minHeight: `${initialHeight}px` } : undefined}
          >
            <ModalContextProvider>{children}</ModalContextProvider>
          </DDSScrollLayout>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Portal>
  );
}

export type DDSDialogHeaderProps = {
  title: TranslatedString | ReactNode;
  /**
   * Whether to hide the title visually. Keeps it in the DOM for accessibility.
   */
  visuallyHideTitle?: boolean;
  hideBorder?: boolean;
  className?: string;
  onBack?: () => void;
  removeCloseButton?: boolean;
  closeButtonVariant?: React.ComponentProps<typeof DDSIconButton>["variant"];
  /** Preserve an existing close action's analytics name when migrating a dialog. */
  closeButtonDataName?: string;
  /**
   * If you're managing your modal state yourself, you can pass in an onClose function to handle the close event
   */
  onClose?: () => void;
  /**
   * Header actions rendered between the title and close button.
   * Should be an array of DDSIconButton or DDSDropdownMenu elements.
   */
  headerActions?: ReactElement[];
  /**
   * When true, positions the close button (and any headerActions) as a floating
   * overlay instead of taking up space in the flex layout. Useful when you need
   * centered content in the header without the close button affecting alignment.
   * Typically used with `visuallyHideTitle` and `children`.
   */
  floatingActions?: boolean;
  "data-name"?: string;
  "event-metadata"?: object;
  experiments?: string[];
  children?: ReactNode;
  subheading?: ReactNode;
  fullscreen?: boolean;
};

function DDSDialogHeader({
  children,
  title,
  className,
  onBack,
  onClose,
  removeCloseButton,
  closeButtonVariant,
  closeButtonDataName,
  visuallyHideTitle,
  subheading,
  hideBorder,
  fullscreen,
  headerActions,
  floatingActions,
  ...props
}: DDSDialogHeaderProps) {
  return (
    <div
      data-scroll-role="header"
      className={clsx(
        classes.header,
        (children || subheading) && classes.headerHasChildren,
        hideBorder && classes.noBorder,
        className,
      )}
      {...props}
    >
      <div
        className={clsx(
          visuallyHideTitle ? classes.titleBarHideTitle : classes.titleBar,
          !onBack && visuallyHideTitle && !removeCloseButton ? classes.headerRightAligned : classes.headerSpaceBetween,
          fullscreen && classes.headerFullscreen,
          floatingActions && classes.floatingActionsTitleBar,
        )}
      >
        {onBack && (
          <div className={classes.headerLeft}>
            <DDSIconButton
              variant="gray"
              size="small"
              onClick={onBack}
              data-name="dialog.back"
              tooltip={{ content: "Back", dataName: "dialog.backTooltip" }}
              aria-label={autoTranslate("Go back")}
            >
              <IconArrowLeft size="s" />
            </DDSIconButton>
          </div>
        )}
        <div className={classes.titleContainer}>
          {visuallyHideTitle ? (
            <>
              <VisuallyHidden.Root>
                <Dialog.Title className={classes.title} tabIndex={0}>
                  {title}
                </Dialog.Title>
              </VisuallyHidden.Root>
              {children}
            </>
          ) : (
            <Dialog.Title className={classes.title} tabIndex={0}>
              {title}
            </Dialog.Title>
          )}
          {subheading}
        </div>
        <div className={classes.headerRight}>
          {headerActions}
          {removeCloseButton ? (
            <div className={classes.noClose} />
          ) : (
            <DDSDialogHeaderCloseButton onClose={onClose} variant={closeButtonVariant} dataName={closeButtonDataName} />
          )}
        </div>
      </div>
      {visuallyHideTitle ? null : children}
    </div>
  );
}
DDSDialogHeader.displayName = "DDSDialogHeader";

function DDSDialogHeaderCloseButton({
  onClose,
  variant = "gray",
  dataName = "dialog.close",
}: {
  onClose?: () => void;
  variant?: React.ComponentProps<typeof DDSIconButton>["variant"];
  dataName?: string;
}) {
  const button = (
    <DDSIconButton
      variant={variant}
      size="small"
      onClick={onClose}
      data-name={dataName}
      tooltip={{ content: "Close", dataName: "dialog.closeTooltip" }}
      aria-label={autoTranslate("Close")}
    >
      <IconX size="s" />
    </DDSIconButton>
  );

  return onClose ? (
    button
  ) : (
    <Dialog.Close aria-label={autoTranslate("Close")} asChild>
      {button}
    </Dialog.Close>
  );
}

export type DDSDialogImageHeaderProps = {
  title: TranslatedString | ReactNode;
  className?: string;
  onBack?: () => void;
  removeCloseButton?: boolean;
  onClose?: () => void;
  /**
   * Header actions rendered between the title and close button.
   * Should be an array of DDSIconButton or DDSDropdownMenu elements.
   */
  headerActions?: ReactElement[];
  "data-name"?: string;
  "event-metadata"?: object;
  experiments?: string[];
  children: ReactNode;
};
function DDSDialogImageHeader({
  children,
  title,
  className,
  onBack,
  onClose,
  removeCloseButton,
  headerActions,
  ...props
}: DDSDialogImageHeaderProps) {
  const { onOpenChange } = useDialogContext();
  const dataName = props["data-name"];

  const handleOnClose = () => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  };

  const closeButtonDataName = dataName ? `${dataName}.close` : "dialog.close";

  return (
    <div data-scroll-role="header" className={clsx(classes.headerImage, className)} {...props}>
      <div className={classes.headerImageButtons}>
        {onBack && (
          <div className={classes.headerLeft}>
            <DDSIconButton
              variant="gray"
              size="small"
              onClick={onBack}
              data-name="dialog.back"
              tooltip={{ content: "Back", dataName: "dialog.backTooltip" }}
              aria-label={autoTranslate("Go back")}
            >
              <IconArrowLeft size="s" />
            </DDSIconButton>
          </div>
        )}
        <div className={classes.titleContainer}>
          <VisuallyHidden.Root>
            <Dialog.Title className={classes.title}>{title}</Dialog.Title>
          </VisuallyHidden.Root>
        </div>
        <div className={classes.headerRight}>
          {headerActions}
          {removeCloseButton ? (
            <div className={classes.noClose} />
          ) : onClose ? (
            <DDSIconButton
              variant="media"
              size="xsmall"
              onClick={handleOnClose}
              data-name={closeButtonDataName}
              tooltip={{ content: "Close", dataName: "dialog.closeTooltip" }}
              aria-label={autoTranslate("Close")}
            >
              <IconX size="s" />
            </DDSIconButton>
          ) : (
            <Dialog.Close aria-label={autoTranslate("Close")} asChild>
              <DDSIconButton
                variant="media"
                size="xsmall"
                data-name={closeButtonDataName}
                tooltip={{ content: "Close", dataName: "dialog.closeTooltip" }}
                aria-label={autoTranslate("Close")}
              >
                <IconX size="s" />
              </DDSIconButton>
            </Dialog.Close>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
DDSDialogImageHeader.displayName = "DDSDialogImageHeader";

interface DDSDialogBodyProps extends Omit<React.ComponentPropsWithoutRef<"div">, "className" | "onScroll"> {
  children: ReactNode;
  className?: string;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
}

function DDSDialogBody({ children, className, onScroll, ...rest }: DDSDialogBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  function updateScrollable() {
    if (bodyRef.current) {
      // Either axis: a body that only scrolls sideways is just as unreachable
      // by keyboard, and axe's scrollable-region-focusable rule flags it too.
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = bodyRef.current;
      setIsScrollable(scrollHeight > clientHeight || scrollWidth > clientWidth);
    }
  }

  useResizeObserver({
    ref: bodyRef,
    onResize: updateScrollable,
  });

  // Run once on mount; ResizeObserver handles subsequent updates
  useLayoutEffect(updateScrollable, []);

  return (
    <div
      ref={bodyRef}
      data-scroll-role="body"
      className={clsx(classes.body, className)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Scrollable content needs tabIndex for keyboard accessibility
      tabIndex={isScrollable ? 0 : undefined}
      onScroll={onScroll}
      {...rest}
    >
      {children}
    </div>
  );
}

function DDSDialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div data-scroll-role="footer" className={clsx(classes.footer, className)}>
      {children}
    </div>
  );
}

function DDSDialogCloseButton({
  children,
  "data-name": dataName,
  variant = "secondary",
  size = "medium",
  onClick,
}: {
  children: ReactNode;
  "data-name": string;
  variant?: (typeof Variants)[number];
  size?: (typeof Sizes)[number];
  onClick?: () => void;
}) {
  return (
    <Dialog.Close asChild>
      <DDSButton data-name={dataName} variant={variant} size={size} onClick={onClick}>
        {children}
      </DDSButton>
    </Dialog.Close>
  );
}

export {
  DDSDialog,
  DDSDialogTrigger,
  DDSDialogContent,
  DDSDialogHeader,
  DDSDialogFooter,
  DDSDialogCloseButton,
  DDSDialogBody,
  DDSDialogImageHeader,
};
